/**
 * Data «B» — a figure the platform does not publish yet — drawn the same way everywhere. The
 * designer's brief part 1 §4 (DEC-85, DEC-95), `docs/adr/9033`.
 *
 *   table   the column is there, its header carries the «In arrivo» tag, every cell says «Non ancora
 *           pubblicato» in words
 *   KPI     «Non ancora pubblicato» in secondary ink, with «In arrivo» under it
 *   chart   the hatched silhouette of the plot, with the tag
 *
 * Words, not a dash (ADR 9061, 2026-10-13): a client read a dash and a superscript as minus signs. The
 * glyph stays only on the ghost silhouette (ADR 9013), which is not an absence.
 *
 * None of these says WHY. The reason is a sentence, and it is said once, in the page's strip
 * (`PageStatusStrip`): the page registers one `not-published` entry and draws these where the
 * figures will stand. A reason repeated under every tile is the "fifteen notices" ADR 9013
 * removed.
 *
 * Nothing here computes a number, so nothing here can be mistaken for one: the column has no
 * `value` (it cannot be sorted, filtered or exported), the KPI has no figure, the chart has no
 * series.
 */

import { useTranslation } from 'react-i18next';

import { AboutPopover, type ICUValues } from '../primitives';
import type { DataColumn } from './data-table';

/** The hatch the not-published strip and the chart silhouette share: `surface-constants` and `strip-hatch`. */
export const ARRIVING_HATCH =
  'bg-[repeating-linear-gradient(135deg,var(--bata-color-surface-constants)_0_8px,var(--bata-color-strip-hatch)_8px_16px)]';

/** «In arrivo»: ink border on white, 11px 700 uppercase. */
export function ArrivingTag(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span
      data-arriving-tag=""
      className="border-ink-primary bg-surface-raised text-ink-primary inline-flex shrink-0 items-center border px-2 py-[2px] text-[11px] font-bold uppercase leading-[14px] tracking-[0.08em]"
    >
      {t('pageStatus.arriving.tag')}
    </span>
  );
}

/** A column header with the tag beside its name. */
export function ArrivingHeader({ headerKey, headerValues }: {
  readonly headerKey: string;
  readonly headerValues?: ICUValues;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-2">
      <span>{t(headerKey, headerValues ?? {})}</span>
      <ArrivingTag />
    </span>
  );
}

/** One cell of a not-published column: «Not published yet», in words (ADR 9061). */
export function ArrivingCell({ align = 'end' }: { readonly align?: 'start' | 'end' }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <td className={`text-ink-secondary py-1 whitespace-nowrap ${align === 'end' ? 'text-end' : 'text-start'}`}>
      {t('pageStatus.arriving.value')}
    </td>
  );
}

/**
 * A `DataTable` column for a figure the platform does not publish yet. No `kind` and no
 * `value`, so the table offers no sort or filter on it and its CSV leaves it out — a column of
 * words in a file would read as a column of blanks somebody forgot to fill.
 */
export function arrivingColumn<T>({ id, headerKey, headerValues, align = 'end', descriptionKey }: {
  readonly id: string;
  readonly headerKey: string;
  readonly headerValues?: ICUValues;
  readonly align?: 'start' | 'end';
  /** What the column WILL hold once published, and which read is awaited (DEC-111, ADR 9047). */
  readonly descriptionKey?: string;
}): DataColumn<T> {
  return {
    id,
    headerKey,
    ...(headerValues === undefined ? {} : { headerValues }),
    ...(descriptionKey === undefined ? {} : { descriptionKey }),
    align,
    filterable: false,
    header: <ArrivingHeader headerKey={headerKey} {...(headerValues === undefined ? {} : { headerValues })} />,
    cell: () => <ArrivingCell align={align} />,
  };
}

/** A KPI whose figure is not published yet: its label, «Not published yet», and the tag under it. */
export function ArrivingFigure({ labelKey, size = 'metric', descriptionKey }: {
  readonly labelKey: string;
  readonly size?: 'metric' | 'dense';
  /** What the figure WILL be once published (DEC-111, ADR 9047): an (i) after the label. */
  readonly descriptionKey?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const figureClass = size === 'metric'
    ? 'text-metric font-numeric font-medium tabular-nums'
    : 'text-numeric font-numeric tabular-nums';
  return (
    <div className="flex flex-col gap-1" data-arriving-figure="">
      <span className="flex items-center gap-1">
        <span className={size === 'metric' ? 'text-micro text-ink-secondary' : 'text-caption text-ink-secondary'}>
          {t(labelKey)}
        </span>
        {descriptionKey !== undefined && <AboutPopover aboutKeys={[descriptionKey]} title={t(labelKey)} size="sm" />}
      </span>
      <span className={`${figureClass} text-ink-secondary`}>{t('pageStatus.arriving.value')}</span>
      <span><ArrivingTag /></span>
    </div>
  );
}

/**
 * A chart whose series the platform does not publish yet: its title, and the plot's area
 * hatched with the tag at its centre. An image with a name, not a chart: there is no series to
 * tabulate, so there is no table view and no download to disable.
 */
export function ArrivingChart({ titleKey, titleValues, height = 200, descriptionKey }: {
  readonly titleKey: string;
  readonly titleValues?: ICUValues;
  /** What the chart WILL show once published (DEC-111, ADR 9047): an (i) after the title. */
  readonly descriptionKey?: string;
  /** The plot's height in px — the height the real chart will take, so nothing moves when it lands. */
  readonly height?: number;
}): React.JSX.Element {
  const { t } = useTranslation();
  const title = t(titleKey, titleValues ?? {});
  return (
    <figure className="flex flex-col gap-2" data-arriving-chart="">
      <figcaption className="text-label text-ink-primary flex items-center gap-1 font-semibold">
        <span>{title}</span>
        {descriptionKey !== undefined && <AboutPopover aboutKeys={[descriptionKey]} title={title} size="sm" />}
      </figcaption>
      <div
        role="img"
        aria-label={t('pageStatus.arriving.chart', { title })}
        className={`${ARRIVING_HATCH} flex items-center justify-center`}
        style={{ blockSize: height }}
      >
        <ArrivingTag />
      </div>
    </figure>
  );
}
